import React, { useEffect, useState, useRef } from 'react';

import './App.css';

import Timer from './Timer';

interface Row {
  0: number | null | undefined,
  1: number | null | undefined,
  2: number | null | undefined,
  3: number | null | undefined
}

interface Grid {
  0: Row,
  1: Row,
  2: Row,
  3: Row
}

function drawTile(width: number, text: string, ctx: CanvasRenderingContext2D) {
  const radius = 24;
  const deg90 = Math.PI / 2;
  const deg180 = Math.PI;

  ctx.fillStyle = '#4438CF';

  ctx.beginPath();
  // top left corner
  ctx.arc(radius, radius, radius, deg180, -1 * deg90);
  // top right corner
  ctx.arc(width - radius, radius, radius, -1 * deg90, 0);
  // bottom right corner
  ctx.arc(width - radius, width - radius, radius, 0, deg90);
  // bottom left corner
  ctx.arc(radius, width - radius, radius, deg90, deg180);
  ctx.fill();

  ctx.font = '64px -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillStyle = 'white';
  ctx.fillText(text, width / 2, width / 2);
}

function drawGrid(size: number, gridState: any, ctx: CanvasRenderingContext2D) {
  const padding = 0.03 * size;
  const drawnArea = size - (5 * padding);
  const tileWidth = drawnArea / 4;

  for (let row = 0; row < 4; row += 1) {
    ctx.translate(padding, (row + 1) * padding + row * tileWidth);

    for (let col = 0; col < 4; col += 1) {
      if (gridState[row][col] !== null) {
        drawTile(tileWidth, gridState[row][col], ctx);
      }

      ctx.translate(tileWidth + padding, 0);
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
}

function generateRandomGrid() : Grid {
  const numbers = [
    1, 2, 3, 4,
    5, 6, 7, 8,
    9, 10, 11, 12,
    13, 14, 15, null
  ];

  // fisher yates shuffle, preserving empty slot
  for (let i = numbers.length - 2; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * i);
    const temp = numbers[i];
    numbers[i] = numbers[j];
    numbers[j] = temp;
  }

  const grid: Grid = {
    0: {} as Row,
    1: {} as Row,
    2: {} as Row,
    3: {} as Row
  }

  for (let i = 0; i < 4; i += 1) {
    for (let j = 0; j < 4; j += 1) {
      grid[i as keyof Grid][j as keyof Grid] = numbers.shift();
    }
  }

  return grid;
}

function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const size = Math.floor(window.innerHeight * 0.5);

  const [gridState, setGridState] = useState(generateRandomGrid());
  const [startTime, setStartTime] = useState<Date | undefined>();
  const [stopTime, setStopTime] = useState<Date | undefined>();
  const [moveCount, setMoveCount] = useState<number>(0);

  const findEmptySlot = (grid: Grid) : number[] => {
    for (let i = 0; i < 4; i += 1) {
      for (let j = 0; j < 4; j += 1) {
        if (grid[i as keyof Grid][j as keyof Grid] === null) {
          return [i, j];
        }
      }
    }

    return [3, 3];
  }

  function randomizeGrid() {
    setStartTime(undefined);
    setStopTime(undefined);
    setMoveCount(0);
    setGridState(generateRandomGrid());
  }


  useEffect(() => {
    const makeMove = (row: number, col: number) => {
      if (!startTime) { setStartTime(new Date()) }

      let moveCount = 0;
      setGridState((oldGrid) => {
        let [emptyRow, emptyCol] = findEmptySlot(oldGrid);

        const newGrid = {
          0: { ...oldGrid[0] },
          1: { ...oldGrid[1] },
          2: { ...oldGrid[2] },
          3: { ...oldGrid[3] }
        };

        if (row !== emptyRow) {
          let dir = Math.sign(row - emptyRow);
          while (emptyRow !== row) {
            newGrid[emptyRow as keyof Grid][emptyCol as keyof Grid] = (
              newGrid[emptyRow + dir as keyof Grid][emptyCol as keyof Grid]
            );
            newGrid[emptyRow + dir as keyof Grid][emptyCol as keyof Grid] = null;

            emptyRow += dir;
            moveCount += 1;
          }
        } else {
          let dir = Math.sign(col - emptyCol);
          while (emptyCol !== col) {
            newGrid[emptyRow as keyof Grid][emptyCol as keyof Grid] = (
              newGrid[emptyRow as keyof Grid][emptyCol + dir as keyof Grid]
            );
            newGrid[emptyRow as keyof Grid][emptyCol + dir as keyof Grid] = null;

            emptyCol += dir;
            moveCount += 1;
          }
        }

        let solved = true;
        for (let i = 0; i < 4; i += 1) {
          for (let j = 0; j < 4; j += 1) {
            let expected: number | null = (4 * i) + (j + 1);
            if (expected === 16) { expected = null }
            if (newGrid[i as keyof Grid][j as keyof Grid] !== expected) {
              solved = false;
            }
          }
        }

        if (solved) { setStopTime(new Date()) }

        return newGrid;
      });

      setMoveCount((c) => c + moveCount)
    }

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = 2 * canvas.clientWidth;
      canvas.height = 2 * canvas.clientHeight;

      const ctx = canvas.getContext('2d');

      const handleMouseDown = (event: MouseEvent) => {
        if (stopTime) { return }

        const row = Math.floor((4 * event.offsetY) / canvas.clientHeight);
        const col = Math.floor((4 * event.offsetX) / canvas.clientWidth);

        const [emptyRow, emptyCol] = findEmptySlot(gridState);

        if ((row === emptyRow) !== (col === emptyCol)) {
          makeMove(row, col);
        }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        // do nothing when the timer has stopped
        if (stopTime) { return }

        const [row, col] = findEmptySlot(gridState);

        switch (event.key) {
          case 'ArrowUp':
            if (row !== 3) { makeMove(row + 1, col) }
          break;
          case 'ArrowDown':
            if (row !== 0) { makeMove(row - 1, col) }
          break;
          case 'ArrowLeft':
            if (col !== 3) { makeMove(row, col + 1) }
          break;
          case 'ArrowRight':
            if (col !== 0) { makeMove(row, col - 1) }
          break;
        }
      }

      if (ctx) {
        canvas.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);

        ctx.fillStyle = '#D9D9D9';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        drawGrid(size * 2, gridState, ctx);
      }

      return () => {
        if (canvas) {
          canvas.removeEventListener('mousedown', handleMouseDown);
          document.removeEventListener('keydown', handleKeyDown);
        }
      };
    }
  }, [gridState, startTime, stopTime, size]);

  return (
    <div className="App">
      <div className="TimerContainer">
        <Timer startTime={startTime} stopTime={stopTime} />
      </div>
      <div className="MovesContainer">
        <div><strong>Moves</strong></div>
        <div className="MoveCount">{moveCount}</div>
      </div>
      <div className="ButtonRow" style={{ width: size }}>
        <button onClick={randomizeGrid}>
          {stopTime ? 'Play again' : 'Reset'}
        </button>
      </div>
      <div
        className="CanvasContainer"
        style={{ height: size, width: size }}
      >
        <canvas style={{ height: size, width: size }} ref={canvasRef} />
      </div>
      <a href="https://twitter.com/ianburgan" className="ContactLink">
        Developed by Ian Burgan
      </a>
      <div className="Instructions">
        Click tiles or use arrow keys to move
      </div>
    </div>
  );
}

export default App;
